import { useNavigate } from "react-router-dom";
import { useSignIn } from "@clerk/react/legacy";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import logoLight from "@/assets/png/logo.png";
import logoDark from "@/assets/png/logo_reversed.png";

interface SignInModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SignInModal({ open, onOpenChange }: SignInModalProps) {
  const navigate = useNavigate();
  const { signIn } = useSignIn();

  const handleGoogleSignIn = async () => {
    await signIn?.authenticateWithRedirect({
      strategy: "oauth_google",
      redirectUrl: "/sso-callback",
      redirectUrlComplete: "/dashboard",
    });
  };

  const handleEmailSignIn = () => {
    onOpenChange(false);
    navigate("/login");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] bg-background border-border p-0">
        <VisuallyHidden>
          <DialogTitle>Sign in to Rayloop</DialogTitle>
        </VisuallyHidden>

        <div className="flex flex-col gap-6 p-8">
          <div className="flex items-center gap-2">
            <img src={logoLight} alt="Rayloop" className="h-8 dark:hidden" />
            <img src={logoDark} alt="Rayloop" className="hidden h-8 dark:block" />
          </div>

          <DialogHeader className="space-y-1">
            <h2 className="font-display text-3xl font-bold">Start Looping.</h2>
            <p className="text-muted-foreground text-xl">Log in to your account</p>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleGoogleSignIn}
              className="w-full justify-center gap-3 bg-secondary text-foreground border-border hover:bg-secondary/80 cursor-pointer"
            >
              <svg className="h-5 w-5" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">OR</span>
            </div>
          </div>

          <Button
            variant="default"
            size="lg"
            onClick={handleEmailSignIn}
            className="w-full bg-foreground text-background hover:bg-foreground/90 cursor-pointer"
          >
            Continue with email
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
